import { AsyncPipe } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { doc, DocumentSnapshot, Firestore, getDoc } from "@angular/fire/firestore";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { CatchErrorService } from "@core/catch-error.service";
import { IonicModule } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import * as moment from "moment";
import { combineLatest, from, interval, of } from "rxjs";
import { catchError, delay, map, shareReplay, startWith, switchMap, tap } from "rxjs/operators";
import { CommonVariable } from "src/app/app.constants";
import { HomeRepositoryService } from "src/app/pages/home/home-repository.service";
import { GameBalanceService } from "src/app/services/game-balance.service";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { TrackedEventsService } from "src/app/services/tracked-events.service";
import { ClarityService } from "src/app/services/clarity.service";
import { HomeCharityService } from "src/app/pages/home/home-charity.service";

@Component({
  selector: "app-charity-expand",
  templateUrl: "./charity-expand.component.html",
  styleUrls: ["./charity-expand.component.scss"],
  standalone: true,
  imports: [
    IonicModule,
    MatProgressSpinner,
    MatIcon,
    AsyncPipe,
  ],
})
export class CharityExpandComponent implements OnInit {
  selectedLanguage: string;
  voted: boolean;
  selectedId: number = null;
  timer$: any;
  sub: any;
  spinnerAnimation: boolean;
  progressBar: number;
  proceed = true;
  buttonLabel: string;
  isVoteDisabled: boolean;
  // Fetch user balance details to display in the home page
  userBalanceDetails$ = this.authService?.userCurrentBalance$?.pipe(
    startWith({ points: 0 } as { points: number }),
    delay(0)
  );
  constructor(
    private firestore: Firestore,
    private matDialogRef: MatDialogRef<CharityExpandComponent>,
    public translate: TranslateService,
    private authService: AuthService,
    private trackedEventsService: TrackedEventsService,
    private homeService: HomeRepositoryService,
    private changeDetectorRef: ChangeDetectorRef,
    private GTMSegment: GTMSegmentService,
    private gameBalanceService: GameBalanceService,
    private clarityService: ClarityService,
    private homeCharityService: HomeCharityService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private catchService: CatchErrorService
  ) {
    this.authService?.selectedlanguage$?.subscribe(
      (l) => {
        this.selectedLanguage = l;
        translate.use(l ? l : CommonVariable?.preferredLanguage);
      },
      (err) => {
        return err;
      }
    );

    if (this.data && typeof this.data === 'object' && this.data.index !== undefined) {
      this.buttonLabel = this.data.buttonLabel;
      this.isVoteDisabled = this.data.isVoteDisabled;
      this.data = this.data.index;
    }
  }

  // ========== Charity Data Fetching with Error Handling ========== //

  charityExpandData$ = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (isOnline) {
        const docRef = doc(
          this.firestore,
          `/general_modules/charity_of_month/charity_by_month/${moment(new Date()).format("MMM_YYYY").toLowerCase()}`
        );

        // Wrapping getDoc() in `from()` for RxJS compatibility
        return from(getDoc(docRef)).pipe(
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          catchError((error) => {
            console.error('Error fetching document:', error);
            throw new Error('Failed to fetch charity data'); // Handle the error upstream
          })
        );
      } else {
        console.error('User is offline');
        throw new Error('User is offline'); // Handle offline error upstream
      }
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1)
  ).pipe(
    map((docSnapshot: DocumentSnapshot<Data>) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        console.log("data from charityExpandData", data);
        return data;
      } else {
        throw new Error('Document does not exist'); // Handle missing document scenario
      }
    }),
    catchError((error) => {
      // Log the error and provide a fallback value or behavior
      console.error('Error processing charityExpandData:', error);
      return of({ message: 'Error fetching charity data', error }); // Fallback response
    })
  );

  // ========== Previous Month Charity Data Fetching with Error Handling ========== //
  previousMonthCharity$ = from(
      getDoc(
        doc(
          this.firestore,
          `/general_modules/charity_of_month/charity_by_month/${moment()
            .subtract(1, "months")
            .format("MMM_YYYY")
            .toLowerCase()}`
        )
      )
  ).pipe(
      tap(docSnapshot => {
        if (!docSnapshot.exists()) {
          console.warn('Previous month charity doc does not exist at:', docSnapshot);
        }
      }),
      map(docSnapshot => {
        if (docSnapshot.exists()) {
          return docSnapshot.data();
        }
        return null;  // Return null if document is missing
      }),
      tap(data => {
        return data;
      })
  );
  // ========== Charity Page Data ========== //
  charityPageData$ = combineLatest([
      this.charityExpandData$,
      this.previousMonthCharity$,
    ]).pipe(
      map((res:Data) => {
      const chariryData = res?.[0]?.content ? res[0] : res?.[1]?.content ? res[1] : null;
      return chariryData;
      })
  );
  ngOnInit() {
    this.homeCharityService.voted$.subscribe((voted) => {
      if (voted) {
        this.voted = true;
        this.changeDetectorRef.detectChanges();
      }
    });
  }
  charityVote(charity, i) {
    this.GTMSegment?.HotJarEvent("vote_charity_card");
    if (!window.navigator.onLine) return;
    let isUpdatedCelebration = false;

    this.trackedEventsService?.listOfEvents$?.subscribe(
      (d) => {
        d?.some((f) => f?.id === "vote_to_charity")
          ? this.trackedEventsService.updateTrackedUserEvent("vote_to_charity")
          : null;
      },
      (err) => {
        return err;
      }
    );

    this.trackedEventsService?.listOfEvents$?.subscribe((d) => {
      d.map(
        (data: any) => {
          if (data?.id === "vote_to_charity" && !isUpdatedCelebration) {
            this.trackedEventsService.updateCelebration(data);
            isUpdatedCelebration = true;
          }
        },
        (err) => {
          return err;
        }
      );
    });
    this.gameBalanceService.voteCharity(charity[i]?.award_id, charity[i]?.charity_points)?.subscribe(
      (data) => {
        if (data?.status === "rejected") {
          this.authService
            .getConstantsMsg(data?.error_code)
            .then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: "Something went wrong!",
                  line_2: "",
                },
                style: {
                  type: "Error",
                  icon: "tick circle error.svg",
                },
              };
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            })
            .catch((err) => {
              return err;
            });
          charity[i].spinner.spinnerAnimation = false;
          return;
        }
        if (data?.status === "FAIL") {
          this.authService.getConstantsMsg("1164").then((translatedMsg) => {
                  const defaultMsg = {
                    message: {
                      line_1: "You do not have enough points to vote",
                      line_2: "",
                    },
                    style: {
                      icon: "tick circle error.svg",
                      type: "Error",
                    },
                   };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          }).catch((err) => {
            return err;
          });
            charity[i].spinner.spinnerAnimation = false;
            return;
          }

        if (data.status === "success") {
          this.clarityService.event("vote_charity");
          charity[i].spinner.progressBar = 100;

          // Rehydrate shared member points balance after confirmed learn-more
          // popup vote success (LIVE adaptation of UAT applyVoteSuccess refresh).
          this.authService.refreshUserCurrentBalance();

          this.homeService.updateCharityData(i, charity[i]?.reference);

          setTimeout(() => {
            charity[i].spinner.spinnerAnimation = false;
            charity[i].spinner.proceed = false;
            this.changeDetectorRef.detectChanges();
            setTimeout(() => {
              charity[i].spinner.proceed = true;
              this.voted = true;
              this.matDialogRef.close(true);
            }, 500);
          }, 300);
        } else {
          charity[i].spinner.spinnerAnimation = false;
        }
      },
      (err) => {
        return err;
      }
    );
  }
  startSpinner(charity, i) {
    charity[i].spinner.spinnerAnimation = true;
    charity[i].spinner.progressBar = 0;
    this.timer$ = interval(20);
    this.sub = this.timer$?.subscribe(
      (sec) => {
        charity[i].spinner.progressBar = 0 + sec;
      },
      (err) => {
        return err;
      }
    );
  }
}
